import React from 'react';
import { BarChart3Icon, FileTextIcon, CheckCircleIcon, AlertTriangleIcon, ClockIcon, ArrowUpIcon, ArrowDownIcon, DollarSignIcon, FileSignatureIcon, CalendarIcon, MinusSquareIcon, TrendingUpIcon, TrendingDownIcon, AlertCircleIcon } from 'lucide-react';
import { formatCurrency } from '../utils/currencyFormatter';
interface APDashboardProps {
  invoices: any[];
  setCurrentView: (view: string) => void;
  locale?: string;
}
export function APDashboard({
  invoices,
  setCurrentView,
  locale = 'en-US'
}: APDashboardProps) {
  // Calculate invoice statistics
  const totalInvoices = invoices.length;
  const pendingApproval = invoices.filter(inv => inv.status === 'Pending Approval').length;
  const approved = invoices.filter(inv => inv.status === 'Approved').length;
  const paid = invoices.filter(inv => inv.status === 'Paid').length;
  // Calculate total invoice amount
  const totalInvoiceAmount = invoices.reduce((sum, inv) => sum + inv.total, 0);
  // Sample data for credit notes
  const creditNotes = [{
    id: 'CN-2023-001',
    creditNoteNumber: 'CN-2023-001',
    vendor: 'Office Depot',
    creditNoteDate: '2023-10-15',
    amount: 750.25,
    currency: 'USD',
    status: 'Draft'
  }, {
    id: 'CN-2023-002',
    creditNoteNumber: 'CN-2023-002',
    vendor: 'Dell Technologies',
    creditNoteDate: '2023-10-12',
    amount: 430.0,
    currency: 'USD',
    status: 'Approved'
  }];
  // Sample data for RCTIs
  const rctis = [{
    id: 'RCTI-2023-001',
    rctiNumber: 'RCTI-2023-001',
    vendor: 'Supplier Co.',
    rctiDate: '2023-10-10',
    amount: 1200.5,
    currency: 'USD',
    status: 'Pending'
  }, {
    id: 'RCTI-2023-002',
    rctiNumber: 'RCTI-2023-002',
    vendor: 'Service Provider Ltd',
    rctiDate: '2023-10-08',
    amount: 850.75,
    currency: 'USD',
    status: 'Posted'
  }];
  // Calculate credit note statistics
  const totalCreditNotes = creditNotes.length;
  const totalCreditNoteAmount = creditNotes.reduce((sum, cn) => sum + cn.amount, 0);
  // Calculate RCTI statistics
  const totalRCTIs = rctis.length;
  const totalRCTIAmount = rctis.reduce((sum, rcti) => sum + rcti.amount, 0);
  // Calculate overall AP statistics
  const totalDocuments = totalInvoices + totalCreditNotes + totalRCTIs;
  const totalDebits = totalInvoiceAmount + totalRCTIAmount;
  const totalCredits = totalCreditNoteAmount;
  const netAmount = totalDebits - totalCredits;
  // Status counts
  const documentStatusCounts = {
    'Pending Approval': pendingApproval + creditNotes.filter(cn => cn.status === 'Pending').length + rctis.filter(r => r.status === 'Pending').length,
    Approved: approved + creditNotes.filter(cn => cn.status === 'Approved').length + rctis.filter(r => r.status === 'Approved').length,
    'Paid/Posted': paid + creditNotes.filter(cn => cn.status === 'Posted').length + rctis.filter(r => r.status === 'Posted').length,
    Draft: invoices.filter(inv => inv.status === 'Draft').length + creditNotes.filter(cn => cn.status === 'Draft').length + rctis.filter(r => r.status === 'Draft').length,
    'Failed Compliance': 3 // Sample data
  };
  // Get recent documents (combine and sort all document types)
  const recentDocuments = [...invoices.map(inv => ({
    ...inv,
    type: 'Invoice',
    number: inv.invoiceNumber,
    date: inv.invoiceDate,
    amount: inv.total
  })), ...creditNotes.map(cn => ({
    ...cn,
    type: 'Credit Note',
    number: cn.creditNoteNumber,
    date: cn.creditNoteDate
  })), ...rctis.map(rcti => ({
    ...rcti,
    type: 'RCTI',
    number: rcti.rctiNumber,
    date: rcti.rctiDate
  }))].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);
  return <div data-id="element-330">
      <div className="mb-8 flex justify-between items-center" data-id="element-331">
        <div data-id="element-332">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-333">AP Dashboard</h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-334">
            Overview of invoices, credit notes, and RCTIs
          </p>
        </div>
        <div className="flex space-x-2" data-id="element-335">
          <button onClick={() => setCurrentView('invoice-entry')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-336">
            <FileTextIcon className="h-4 w-4 mr-1" data-id="element-337" />
            New Invoice
          </button>
          <button onClick={() => setCurrentView('credit-note-entry')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-338">
            <MinusSquareIcon className="h-4 w-4 mr-1" data-id="element-339" />
            New Credit Note
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8" data-id="element-340">
        {/* Total Documents */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-341">
          <div className="px-4 py-5 sm:p-6" data-id="element-342">
            <div className="flex items-center" data-id="element-343">
              <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3" data-id="element-344">
                <FileTextIcon className="h-6 w-6 text-white" data-id="element-345" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-346">
                <dl data-id="element-347">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-348">
                    Total Documents
                  </dt>
                  <dd className="flex items-baseline" data-id="element-349">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-350">
                      {totalDocuments}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-351">
            <div className="text-sm" data-id="element-352">
              <a href="#" onClick={() => setCurrentView('invoice-list')} className="font-medium text-indigo-600 hover:text-indigo-500" data-id="element-353">
                View all documents
              </a>
            </div>
          </div>
        </div>

        {/* Net Amount */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-354">
          <div className="px-4 py-5 sm:p-6" data-id="element-355">
            <div className="flex items-center" data-id="element-356">
              <div className="flex-shrink-0 bg-green-500 rounded-md p-3" data-id="element-357">
                <DollarSignIcon className="h-6 w-6 text-white" data-id="element-358" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-359">
                <dl data-id="element-360">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-361">
                    Net Amount
                  </dt>
                  <dd className="flex items-baseline" data-id="element-362">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-363">
                      {formatCurrency(netAmount, 'USD', locale)}
                    </div>
                    {netAmount > 0 ? <span className="ml-2 flex items-baseline text-sm font-semibold text-green-600" data-id="element-364">
                        <TrendingUpIcon className="self-center flex-shrink-0 h-4 w-4 text-green-500" data-id="element-365" />
                        <span className="sr-only" data-id="element-366">Increased by</span>
                      </span> : <span className="ml-2 flex items-baseline text-sm font-semibold text-red-600" data-id="element-367">
                        <TrendingDownIcon className="self-center flex-shrink-0 h-4 w-4 text-red-500" data-id="element-368" />
                        <span className="sr-only" data-id="element-369">Decreased by</span>
                      </span>}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-370">
            <div className="text-sm flex justify-between" data-id="element-371">
              <span className="text-gray-500" data-id="element-372">
                Debits: {formatCurrency(totalDebits, 'USD', locale)}
              </span>
              <span className="text-gray-500" data-id="element-373">
                Credits: {formatCurrency(totalCredits, 'USD', locale)}
              </span>
            </div>
          </div>
        </div>

        {/* Pending Approval */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-374">
          <div className="px-4 py-5 sm:p-6" data-id="element-375">
            <div className="flex items-center" data-id="element-376">
              <div className="flex-shrink-0 bg-yellow-500 rounded-md p-3" data-id="element-377">
                <ClockIcon className="h-6 w-6 text-white" data-id="element-378" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-379">
                <dl data-id="element-380">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-381">
                    Pending Approval
                  </dt>
                  <dd className="flex items-baseline" data-id="element-382">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-383">
                      {documentStatusCounts['Pending Approval']}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-384">
            <div className="text-sm" data-id="element-385">
              <a href="#" className="font-medium text-yellow-600 hover:text-yellow-500" data-id="element-386">
                Review pending documents
              </a>
            </div>
          </div>
        </div>

        {/* Failed Compliance */}
        <div className="bg-white overflow-hidden shadow rounded-lg" data-id="element-387">
          <div className="px-4 py-5 sm:p-6" data-id="element-388">
            <div className="flex items-center" data-id="element-389">
              <div className="flex-shrink-0 bg-red-500 rounded-md p-3" data-id="element-390">
                <AlertCircleIcon className="h-6 w-6 text-white" data-id="element-391" />
              </div>
              <div className="ml-5 w-0 flex-1" data-id="element-392">
                <dl data-id="element-393">
                  <dt className="text-sm font-medium text-gray-500 truncate" data-id="element-394">
                    Failed Compliance
                  </dt>
                  <dd className="flex items-baseline" data-id="element-395">
                    <div className="text-2xl font-semibold text-gray-900" data-id="element-396">
                      {documentStatusCounts['Failed Compliance']}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-4 sm:px-6" data-id="element-397">
            <div className="text-sm" data-id="element-398">
              <a href="#" className="font-medium text-red-600 hover:text-red-500" data-id="element-399">
                Resolve compliance issues
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Document Type Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8" data-id="element-400">
        {/* Invoices */}
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-401">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-402">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-403">
              Invoices
            </h3>
            <button onClick={() => setCurrentView('invoice-list')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-404">
              View all
            </button>
          </div>
          <div className="p-6" data-id="element-405">
            <div className="flex justify-between mb-4" data-id="element-406">
              <div className="text-center" data-id="element-407">
                <div className="text-2xl font-bold text-gray-900" data-id="element-408">
                  {totalInvoices}
                </div>
                <div className="text-sm text-gray-500" data-id="element-409">Total</div>
              </div>
              <div className="text-center" data-id="element-410">
                <div className="text-2xl font-bold text-gray-900" data-id="element-411">
                  {formatCurrency(totalInvoiceAmount, 'USD', locale)}
                </div>
                <div className="text-sm text-gray-500" data-id="element-412">Amount</div>
              </div>
              <div className="text-center" data-id="element-413">
                <div className="text-2xl font-bold text-yellow-500" data-id="element-414">
                  {pendingApproval}
                </div>
                <div className="text-sm text-gray-500" data-id="element-415">Pending</div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-416">
              <div className="bg-indigo-600 h-2.5 rounded-full" style={{
              width: `${approved / totalInvoices * 100}%`
            }} data-id="element-417"></div>
            </div>
            <div className="space-y-4 mt-4" data-id="element-418">
              <div className="flex items-center justify-between" data-id="element-419">
                <div className="flex items-center" data-id="element-420">
                  <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2" data-id="element-421"></div>
                  <span className="text-sm text-gray-500" data-id="element-422">Pending</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-423">
                  {pendingApproval}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-424">
                <div className="flex items-center" data-id="element-425">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-426"></div>
                  <span className="text-sm text-gray-500" data-id="element-427">Approved</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-428">
                  {approved}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-429">
                <div className="flex items-center" data-id="element-430">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-2" data-id="element-431"></div>
                  <span className="text-sm text-gray-500" data-id="element-432">Paid</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-433">
                  {paid}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Credit Notes */}
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-434">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-435">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-436">
              Credit Notes
            </h3>
            <button onClick={() => setCurrentView('credit-note-list')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-437">
              View all
            </button>
          </div>
          <div className="p-6" data-id="element-438">
            <div className="flex justify-between mb-4" data-id="element-439">
              <div className="text-center" data-id="element-440">
                <div className="text-2xl font-bold text-gray-900" data-id="element-441">
                  {totalCreditNotes}
                </div>
                <div className="text-sm text-gray-500" data-id="element-442">Total</div>
              </div>
              <div className="text-center" data-id="element-443">
                <div className="text-2xl font-bold text-gray-900" data-id="element-444">
                  {formatCurrency(totalCreditNoteAmount, 'USD', locale)}
                </div>
                <div className="text-sm text-gray-500" data-id="element-445">Amount</div>
              </div>
              <div className="text-center" data-id="element-446">
                <div className="text-2xl font-bold text-green-500" data-id="element-447">
                  {creditNotes.filter(cn => cn.status === 'Approved').length}
                </div>
                <div className="text-sm text-gray-500" data-id="element-448">Approved</div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-449">
              <div className="bg-green-600 h-2.5 rounded-full" style={{
              width: `${creditNotes.filter(cn => cn.status === 'Approved').length / totalCreditNotes * 100}%`
            }} data-id="element-450"></div>
            </div>
            <div className="space-y-4 mt-4" data-id="element-451">
              <div className="flex items-center justify-between" data-id="element-452">
                <div className="flex items-center" data-id="element-453">
                  <div className="w-3 h-3 rounded-full bg-gray-300 mr-2" data-id="element-454"></div>
                  <span className="text-sm text-gray-500" data-id="element-455">Draft</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-456">
                  {creditNotes.filter(cn => cn.status === 'Draft').length}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-457">
                <div className="flex items-center" data-id="element-458">
                  <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2" data-id="element-459"></div>
                  <span className="text-sm text-gray-500" data-id="element-460">Pending</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-461">
                  {creditNotes.filter(cn => cn.status === 'Pending').length}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-462">
                <div className="flex items-center" data-id="element-463">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-464"></div>
                  <span className="text-sm text-gray-500" data-id="element-465">Approved</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-466">
                  {creditNotes.filter(cn => cn.status === 'Approved').length}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* RCTIs */}
        <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-467">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-468">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-469">
              RCTIs
            </h3>
            <button onClick={() => setCurrentView('rcti-management')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-470">
              View all
            </button>
          </div>
          <div className="p-6" data-id="element-471">
            <div className="flex justify-between mb-4" data-id="element-472">
              <div className="text-center" data-id="element-473">
                <div className="text-2xl font-bold text-gray-900" data-id="element-474">
                  {totalRCTIs}
                </div>
                <div className="text-sm text-gray-500" data-id="element-475">Total</div>
              </div>
              <div className="text-center" data-id="element-476">
                <div className="text-2xl font-bold text-gray-900" data-id="element-477">
                  {formatCurrency(totalRCTIAmount, 'USD', locale)}
                </div>
                <div className="text-sm text-gray-500" data-id="element-478">Amount</div>
              </div>
              <div className="text-center" data-id="element-479">
                <div className="text-2xl font-bold text-blue-500" data-id="element-480">
                  {rctis.filter(r => r.status === 'Posted').length}
                </div>
                <div className="text-sm text-gray-500" data-id="element-481">Posted</div>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-4" data-id="element-482">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{
              width: `${rctis.filter(r => r.status === 'Posted').length / totalRCTIs * 100}%`
            }} data-id="element-483"></div>
            </div>
            <div className="space-y-4 mt-4" data-id="element-484">
              <div className="flex items-center justify-between" data-id="element-485">
                <div className="flex items-center" data-id="element-486">
                  <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2" data-id="element-487"></div>
                  <span className="text-sm text-gray-500" data-id="element-488">Pending</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-489">
                  {rctis.filter(r => r.status === 'Pending').length}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-490">
                <div className="flex items-center" data-id="element-491">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-492"></div>
                  <span className="text-sm text-gray-500" data-id="element-493">Approved</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-494">
                  {rctis.filter(r => r.status === 'Approved').length}
                </span>
              </div>
              <div className="flex items-center justify-between" data-id="element-495">
                <div className="flex items-center" data-id="element-496">
                  <div className="w-3 h-3 rounded-full bg-blue-500 mr-2" data-id="element-497"></div>
                  <span className="text-sm text-gray-500" data-id="element-498">Posted</span>
                </div>
                <span className="text-sm font-medium text-gray-900" data-id="element-499">
                  {rctis.filter(r => r.status === 'Posted').length}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Documents */}
      <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-500">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6 flex justify-between items-center" data-id="element-501">
          <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-502">
            Recent Documents
          </h3>
          <div className="flex space-x-2" data-id="element-503">
            <button onClick={() => setCurrentView('invoice-list')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-504">
              Invoices
            </button>
            <button onClick={() => setCurrentView('credit-note-list')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-505">
              Credit Notes
            </button>
            <button onClick={() => setCurrentView('rcti-management')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-506">
              RCTIs
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-507">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-508">
            <thead className="bg-gray-50" data-id="element-509">
              <tr data-id="element-510">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-511">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-512">
                  Number
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-513">
                  Vendor
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-514">
                  Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-515">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-516">
                  Status
                </th>
                <th scope="col" className="relative px-6 py-3" data-id="element-517">
                  <span className="sr-only" data-id="element-518">View</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-519">
              {recentDocuments.length > 0 ? recentDocuments.map(doc => <tr key={doc.id} className="hover:bg-gray-50" data-id="element-520">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-521">
                      <div className="flex items-center" data-id="element-522">
                        {doc.type === 'Invoice' ? <FileTextIcon className="h-5 w-5 text-indigo-500 mr-2" data-id="element-523" /> : doc.type === 'Credit Note' ? <MinusSquareIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-524" /> : <FileSignatureIcon className="h-5 w-5 text-blue-500 mr-2" data-id="element-525" />}
                        <span className="text-sm text-gray-900" data-id="element-526">
                          {doc.type}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-527">
                      {doc.number}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-528">
                      {doc.vendor}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-529">
                      {new Date(doc.date).toLocaleDateString(locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-530">
                      {formatCurrency(doc.amount, doc.currency, locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-531">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${doc.status === 'Approved' ? 'bg-green-100 text-green-800' : doc.status === 'Pending Approval' || doc.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : doc.status === 'Draft' ? 'bg-gray-100 text-gray-800' : doc.status === 'Paid' || doc.status === 'Posted' ? 'bg-blue-100 text-blue-800' : 'bg-red-100 text-red-800'}`} data-id="element-532">
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-533">
                      <a href="#" onClick={() => {
                  if (doc.type === 'Invoice') {
                    setCurrentView('invoice-list');
                  } else if (doc.type === 'Credit Note') {
                    setCurrentView('credit-note-list');
                  } else {
                    setCurrentView('rcti-management');
                  }
                }} className="text-indigo-600 hover:text-indigo-900" data-id="element-534">
                        View
                      </a>
                    </td>
                  </tr>) : <tr data-id="element-535">
                  <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-536">
                    No documents found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}